import { useState } from "react";
import type { FormEvent } from "react";
import * as api from "../api";
import { ApiError } from "../api";
import { useSession } from "../session";
import { LIMBI, useLimba, useT } from "../i18n";
import * as voce from "../asistent/voce";
import type { Limba } from "../i18n";

export default function Profile() {
  const { user, setUser } = useSession();
  const t = useT();
  const limba = useLimba();
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [savedName, setSavedName] = useState(false);
  const [languageError, setLanguageError] = useState("");

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savedPassword, setSavedPassword] = useState(false);
  const [citeste, setCiteste] = useState(voce.citireaPornita);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSavedName(false);
    setUser(await api.updateProfile({ full_name: fullName.trim() }));
    setSavedName(true);
  }

  /** Limba se salvează la ALEGERE, nu la un buton «Salvează».
   *
   *  E singurul câmp din ecran al cărui efect se vede pe loc — interfața se
   *  rescrie sub mână —, deci un buton de confirmare ar cere o apăsare pentru
   *  ceva ce omul vede deja că s-a întâmplat. Sesiunea primește userul întors
   *  de server, iar `LimbaProvider` comută de acolo: nu scriem limba în două
   *  locuri.
   */
  async function alegeLimba(cod: Limba) {
    if (cod === limba) return;
    setLanguageError("");
    try {
      setUser(await api.updateProfile({ language: cod }));
    } catch {
      setLanguageError(t("profil.limba_esec"));
    }
  }

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError("");
    setSavedPassword(false);
    try {
      await api.changePassword(current, next);
      setCurrent("");
      setNext("");
      setSavedPassword(true);
    } catch (err) {
      setPasswordError(
        err instanceof ApiError
          ? err.code === "invalid_credentials"
            ? t("profil.parola_gresita")
            : err.code === "weak_password"
              ? err.message
              : t("profil.parola_esec")
          : t("profil.parola_esec"),
      );
    }
  }

  return (
    <div className="page" style={{ maxWidth: 520 }}>
      <div className="page-head">
        <div className="disp">{t("profil.titlu")}</div>
      </div>

      <form className="card" style={{ padding: "20px 22px" }} onSubmit={saveProfile}>
        <label className="field">
          <span className="lbl">{t("comun.email")}</span>
          {/* Emailul e și identitatea de login, deci schimbarea lui cere
              confirmare pe adresa nouă — nu un câmp de formular. */}
          <input value={user?.email ?? ""} readOnly />
        </label>

        <label className="field">
          <span className="lbl">{t("comun.nume")}</span>
          <input value={fullName} onChange={(e) => { setFullName(e.target.value); setSavedName(false); }} />
        </label>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button className="btn btnp" type="submit">{t("comun.salveaza")}</button>
          {savedName && <span className="note">{t("profil.salvat")}</span>}
        </div>
      </form>

      <h2 className="section">{t("profil.limba_sectiune")}</h2>

      {/* Numele limbilor NU se traduc: o limbă se caută în lista asta după cum
          se scrie EA, nu după cum o numește limba curentă. Cine deschide
          platforma pe românește și vrea engleză caută «English». */}
      <div className="card" style={{ padding: "20px 22px" }}>
        <label className="field">
          <span className="lbl">{t("profil.limba_eticheta")}</span>
          <select value={limba} onChange={(e) => alegeLimba(e.target.value as Limba)}>
            {LIMBI.map((l) => (
              <option key={l.cod} value={l.cod}>{l.nume}</option>
            ))}
          </select>
        </label>
        {languageError && <p className="note bad" style={{ marginTop: 12 }}>{languageError}</p>}
        <p className="note" style={{ marginTop: 12 }}>{t("profil.limba_nota")}</p>
      </div>

      {voce.sePoateCiti() && (
        <>
          <h2 className="section">{t("profil.voce_sectiune")}</h2>
          <div className="card" style={{ padding: "20px 22px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={citeste}
                     onChange={(e) => { setCiteste(e.target.checked); voce.tineMinteCitirea(e.target.checked); }} />
              <span>{t("profil.voce_citeste")}</span>
            </label>
            <p className="note" style={{ marginTop: 12 }}>{t("profil.voce_nota")}</p>
          </div>
        </>
      )}

      <h2 className="section">{t("profil.parola_sectiune")}</h2>

      <form className="card" style={{ padding: "20px 22px" }} onSubmit={savePassword}>
        <label className="field">
          <span className="lbl">{t("profil.parola_curenta")}</span>
          <input type="password" autoComplete="current-password" value={current} required
                 onChange={(e) => setCurrent(e.target.value)} />
        </label>

        <label className="field">
          <span className="lbl">{t("profil.parola_noua")}</span>
          <input type="password" autoComplete="new-password" value={next} required minLength={10}
                 onChange={(e) => setNext(e.target.value)} />
        </label>

        {passwordError && <p className="note bad" style={{ marginTop: 12 }}>{passwordError}</p>}

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
          <button className="btn btnp" type="submit">{t("profil.schimba_parola")}</button>
          {savedPassword && <span className="note">{t("profil.parola_schimbata")}</span>}
        </div>

        <p className="note" style={{ marginTop: 12 }}>{t("profil.parola_nota")}</p>
      </form>
    </div>
  );
}
